import React, { useState, useContext } from "react";
import {
  FaUserShield,
  FaSun,
  FaMoon,
  FaHome,
  FaSignOutAlt,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaBuilding,
  FaCodeBranch,
  FaSitemap,
  FaUsersCog,
  FaUserTag,
  FaUserFriends,
  FaLayerGroup,
  FaDatabase,
  FaCogs,
  FaClipboardList,
  FaRegClock,
  FaRegCalendarCheck,
  FaPeopleArrows,
} from "react-icons/fa";

import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { useNavigate, useLocation } from "react-router-dom";

// ✅ If your logo is in src/assets:
import logo from "../assets/logo2.jpg";
// ✅ If your logo is in public/assets, comment above and use below:
// const logo = "/assets/logo2.jpg";

const AdminSidebar = ({ isMobileOpen = false, onCloseMobile = () => {} }) => {
  const { isDark, setIsDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("admin-sidebar-collapsed");
    return saved === "true";
  });

  const toggleTheme = (dark) => {
    setIsDark(dark);
    localStorage.setItem("theme-mode", dark ? "dark" : "light");
    if (dark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  };

  const handleToggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem("admin-sidebar-collapsed", newState);
  };

  const handleHomeClick = () => {
    window.location.href = "/temporary-home";
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem("admin");
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userDetails");
      localStorage.removeItem("sessionData");
      sessionStorage.clear();

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      window.location.replace("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
      window.location.replace("/admin/login");
    }
  };

  // ✅ Single-level menu (no submenus)
  // Change these routes to your real routes anytime.
  const menuItems = [
    { label: "Company Master", icon: <FaBuilding />, path: "/adminMaster/companyMaster" },
    { label: "Branch Listing", icon: <FaCodeBranch />, path: "/adminMaster/branch-listing" },
    { label: "Department Master", icon: <FaSitemap />, path: "/adminMaster/DepartmentMasterListing" },
    { label: "User Type Master", icon: <FaUserTag />, path: "/adminMaster/UesrTypeMasterListing" },
    { label: "User Management", icon: <FaUserFriends />, path: "/adminMaster/UserSetupMasterListing" },
    { label: "Bulk Insert", icon: <FaLayerGroup />, path: "/adminMaster/BulkInsertMaster" },
    { label: "Sub Branch Listing", icon: <FaCodeBranch />, path: "/adminMaster/SubbranchListing" },
    { label: "User Log Tracking", icon: <FaRegClock />, path: "/adminMaster/UserLoginDetails" },
    {
      label: "HalfDAy Provision Select Branches",
      icon: <FaRegCalendarCheck />,
      path: "/adminMaster/HalfDayProvisionSelectBranches",
    },
    {
      label: "Payroll Scheme Select Employees",
      icon: <FaPeopleArrows />,
      path: "/adminMaster/PayrollSchemeSelectEmployees",
    },
    { label: "Payroll Customization", icon: <FaCogs />, path: "/adminMaster/PayrollCustomizationNew" },
    { label: "Control Panel Setup", icon: <FaUserShield />, path: "/admin/adminhome" },
    { label: "FinYear DataBase Change", icon: <FaDatabase />, path: "/adminMaster/FinYearDataBaseChange" },
    { label: "Inventory Customization", icon: <FaClipboardList />, path: "/adminMaster/InventoryCustomizationHome" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <div
        className={`
          ${isMobileOpen ? "fixed top-0 left-0 translate-x-0" : "-translate-x-full"}
          sm:translate-x-0 sm:sticky sm:top-0 sm:h-screen sm:flex fixed h-full
          ${collapsed ? "w-20" : "w-80"}
          z-[200] transform transition-all duration-300 flex-col text-white
          ${currentSkin.sidebar}
        `}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-4 border-b border-white/10 dark:border-gray-700/50">
          <div className="flex items-center justify-between">
            <button
              onClick={handleToggleCollapse}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 dark:bg-gray-700/50 backdrop-blur-sm border border-white/20 dark:border-gray-600/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50 transition-all duration-300 shadow-lg"
              title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {collapsed ? <FaAngleDoubleRight className="text-sm" /> : <FaAngleDoubleLeft className="text-sm" />}
            </button>

            <div className="sm:hidden">
              <button
                onClick={onCloseMobile}
                className="text-white/80 hover:text-white text-2xl font-bold transition-colors"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="flex-shrink-0 px-4 py-5 border-b border-white/10 dark:border-gray-700/50">
          <div className="flex flex-col items-center transition-all duration-300">
            <div className={`transition-all duration-300 ${collapsed ? "w-12 h-12" : "w-24 h-24"}`}>
              <img
                src={logo}
                alt="Infraconsoft V5"
                className="w-full h-full rounded-xl object-contain shadow-lg"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/150x150/4F46E5/FFFFFF?text=LOGO";
                }}
              />
            </div>

            {!collapsed && (
              <div className="mt-3 text-center">
                <h2 className="text-lg font-bold text-white dark:text-gray-100">Infraconsoft V5</h2>
                <p className="text-xs text-white/60 dark:text-gray-400">Admin Panel</p>
              </div>
            )}
          </div>
        </div>

        {/* Menu */}
        <div className="flex-1 flex flex-col min-h-0">
          <nav className="flex-1 px-4 overflow-y-auto custom-scrollbar">
            <div className="space-y-2 py-3">
              {menuItems.map((item, idx) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobileOpen) onCloseMobile();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 group border
                      ${
                        active
                          ? "bg-white/20 dark:bg-gray-700/50 shadow-lg border-white/20 dark:border-gray-600/40"
                          : "bg-transparent border-transparent hover:bg-white/10 dark:hover:bg-gray-700/30 hover:border-white/10 dark:hover:border-gray-600/30"
                      }
                    `}
                    title={item.label}
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform duration-200">
                      {item.icon}
                    </span>

                    {!collapsed && (
                      <span className="font-medium text-white/90 dark:text-gray-100 group-hover:text-white">
                        {item.label}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Footer */}
        {!collapsed && (
          <div className="flex-shrink-0 px-4 py-4 border-t border-white/10 dark:border-gray-700/50 bg-white/5 dark:bg-gray-800/20 backdrop-blur-sm">
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleTheme(false)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    !isDark
                      ? "bg-white text-gray-800 shadow-lg"
                      : "bg-white/10 dark:bg-gray-700/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50"
                  }`}
                  title="Light Mode"
                >
                  <FaSun className="text-sm" />
                </button>

                <button
                  onClick={() => toggleTheme(true)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    isDark
                      ? "bg-white text-gray-800 shadow-lg"
                      : "bg-white/10 dark:bg-gray-700/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50"
                  }`}
                  title="Dark Mode"
                >
                  <FaMoon className="text-sm" />
                </button>

                <button
                  onClick={handleHomeClick}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 dark:bg-gray-700/50 text-white hover:bg-white/20 dark:hover:bg-gray-600/50 transition-all duration-200"
                  title="Go to Home"
                >
                  <FaHome className="text-sm" />
                </button>

                <button
                  onClick={handleLogout}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 dark:bg-gray-700/50 text-white hover:bg-red-500/80 dark:hover:bg-red-600/70 transition-all duration-200"
                  title="Logout"
                >
                  <FaSignOutAlt className="text-sm" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 sm:hidden z-[150]" onClick={onCloseMobile}></div>
      )}

      {/* Scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }

        .dark .custom-scrollbar {
          scrollbar-color: rgba(156, 163, 175, 0.5) rgba(75, 85, 99, 0.3);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(75, 85, 99, 0.3);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.5);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.7);
        }
      `}</style>
    </>
  );
};

export default AdminSidebar;
